import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, CalendarClock, CreditCard, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { useLang } from "../i18n";
import { Shell } from "../layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: "", phone: "", car: "" });
  const [enquiries, setEnquiries] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (user === false) return navigate("/login");
    if (!user) return;
    setProfile({ name: user.name || "", phone: user.phone || "", car: user.car || "" });
    fetch(`${API}/enquiries/mine`, { credentials: "include" }).then(r => r.ok ? r.json() : []).then(setEnquiries);
    fetch(`${API}/appointments/mine`, { credentials: "include" }).then(r => r.ok ? r.json() : []).then(setAppointments);
    fetch(`${API}/payments/mine`, { credentials: "include" }).then(r => r.ok ? r.json() : []).then(setPayments);
  }, [user, navigate]);

  if (!user) return <Shell><main className="account-page" /></Shell>;

  const saveProfile = async (e) => {
    e.preventDefault();
    const r = await fetch(`${API}/auth/profile`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(profile) });
    if (!r.ok) return toast.error(t("account.saveErr"));
    setUser(await r.json());
    toast.success(t("account.saved"));
  };

  const signOut = async () => { await logout(); navigate("/"); };

  return (
    <Shell>
      <main className="account-page" data-testid="account-page">
        <div className="account-heading">
          <div>
            <p className="eyebrow">{t("account.eyebrow")}</p>
            <h1>{t("account.hi")} {user.name.split(" ")[0]}.<br /><em>{t("account.garage")}</em></h1>
          </div>
          <button className="button button-outline" onClick={signOut} data-testid="account-logout-button">{t("account.logout")}</button>
        </div>
        <div className="account-grid">
          <div className="form-panel profile-panel">
            <p className="eyebrow">{t("account.details")}</p>
            <form onSubmit={saveProfile} data-testid="profile-form">
              <label>{t("account.name")}<input required minLength="2" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" /></label>
              <label>{t("account.phone")} <span>{t("account.phoneHint")}</span><input value={profile.phone} placeholder="+370 6XX XXXXX" onChange={e => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone-input" /></label>
              <label>{t("account.car")} <span>{t("account.carHint")}</span><input value={profile.car} onChange={e => setProfile({ ...profile, car: e.target.value })} placeholder="BMW 320d (2019)" data-testid="profile-car-input" /></label>
              <button className="button button-red full" data-testid="profile-save-button">{t("account.save")} <ArrowUpRight size={16} /></button>
            </form>
            <Link to="/book" className="button button-outline full" data-testid="account-book-button" style={{ marginTop: 14 }}>{t("account.bookCta")} <CalendarClock size={16} /></Link>
          </div>
          <div className="account-section">
            <h2><CreditCard size={22} /> {t("account.bills")}</h2>
            <div className="inbox-list" data-testid="account-payments-list">
              {payments.length === 0
                ? <div className="empty-state">{t("account.noBills")}</div>
                : payments.map(p => (
                  <article className="inbox-item" key={p.id} data-testid={`payment-${p.id}`}>
                    <div>
                      <span className={`status status-${p.payment_status === "paid" ? "paid" : "pending"}`}>{p.payment_status === "paid" ? t("status.paid") : t("status.awaiting")}</span>
                      <h2>€{p.amount.toFixed(2)} — {p.description}</h2>
                      {p.payment_status !== "paid" && <div className="appointment-actions"><a className="chip chip-link" href={p.checkout_url} data-testid={`pay-${p.id}`}><CreditCard size={12} /> {t("account.payNow")}</a></div>}
                    </div>
                    <span className="inbox-date">{new Date(p.created_at).toLocaleDateString("en-GB")}</span>
                  </article>
                ))}
            </div>
            <h2 style={{ marginTop: 50 }}><CalendarClock size={22} /> {t("account.appointments")}</h2>
            <div className="inbox-list" data-testid="account-appointments-list">
              {appointments.length === 0
                ? <div className="empty-state">{t("account.noAppts")} <Link to="/book" data-testid="account-empty-book-link">{t("account.noApptsCta")}</Link>.</div>
                : appointments.map(a => (
                  <article className="inbox-item" key={a.id} data-testid={`appointment-${a.id}`}>
                    <div>
                      <span className={`status status-${a.status}`}>{t(`status.${a.status}`)}</span>
                      <h2>{a.service}</h2>
                      <p>{a.date} · {a.time}{a.car && ` · ${a.car}`}</p>
                      {a.notes && <p className="message">{a.notes}</p>}
                    </div>
                  </article>
                ))}
            </div>
            <h2 style={{ marginTop: 50 }}><FileText size={22} /> {t("account.enquiries")}</h2>
            <div className="inbox-list" data-testid="account-enquiries-list">
              {enquiries.length === 0
                ? <div className="empty-state">{t("account.noEnquiries")}</div>
                : enquiries.map(item => (
                  <article className="inbox-item" key={item.id} data-testid={`my-enquiry-${item.id}`}>
                    <div>
                      <span className={`status status-${item.status}`}>{t(`status.${item.status}`)}</span>
                      <h2>{item.service}</h2>
                      <p className="message">{item.message}</p>
                    </div>
                    <span className="inbox-date">{new Date(item.created_at).toLocaleDateString("en-GB")}</span>
                  </article>
                ))}
            </div>
          </div>
        </div>
      </main>
    </Shell>
  );
}
